import { nanoid } from 'nanoid';

import { createES256Key } from './dpop';
import { CLIENT_ID, database, REDIRECT_URI } from './environment';
import { AuthorizationError, LoginError } from './errors';
import { generatePKCE } from './pkce';
import { OAuthServerAgent } from './server-agent';
import { storeSession } from './sessions';
import type { AuthorizationServerMetadata, IdentityMetadata, Session } from './types';

export interface AuthorizeOptions {
	metadata: AuthorizationServerMetadata;
	identity?: IdentityMetadata;
	scope: string;
}

/**
 * Build an authorization URL via Pushed Authorization Request (PAR). The state
 * (DPoP key + PKCE verifier) is persisted so it can be recovered on callback.
 */
export const createAuthorizationUrl = async ({
	metadata,
	identity,
	scope,
}: AuthorizeOptions): Promise<URL> => {
	const state = nanoid(24);

	const pkce = await generatePKCE();
	const dpopKey = await createES256Key();

	const params = {
		redirect_uri: REDIRECT_URI,
		code_challenge: pkce.challenge,
		code_challenge_method: pkce.method,
		state: state,
		login_hint: identity?.raw,
		response_mode: 'fragment',
		response_type: 'code',
		display: 'page',
		scope: scope,
	} satisfies Record<string, string | undefined>;

	database.states.set(state, {
		dpopKey: dpopKey,
		metadata: metadata,
		verifier: pkce.verifier,
	});

	const server = new OAuthServerAgent(metadata, dpopKey);
	const response = await server.request('pushed_authorization_request', params);

	const authUrl = new URL(metadata.authorization_endpoint);
	authUrl.searchParams.set('client_id', CLIENT_ID);
	authUrl.searchParams.set('request_uri', response.request_uri);

	return authUrl;
};

/**
 * Finalize authorization given the callback parameters, returning a `Session`.
 */
export const finalizeAuthorization = async (params: URLSearchParams): Promise<Session> => {
	const issuer = params.get('iss');
	const state = params.get('state');
	const code = params.get('code');
	const error = params.get('error');

	if (!state || !(code || error)) {
		throw new LoginError(`missing parameters`);
	}

	const stored = database.states.get(state);
	if (stored) {
		database.states.delete(state);
	} else {
		throw new LoginError(`unknown state provided`);
	}

	const dpopKey = stored.dpopKey;
	const metadata = stored.metadata;

	if (error) {
		throw new AuthorizationError(params.get('error_description') || error);
	}
	if (!code) {
		throw new LoginError(`missing code parameter`);
	}

	if (issuer === null) {
		throw new LoginError(`missing issuer parameter`);
	} else if (issuer !== metadata.issuer) {
		throw new LoginError(`issuer mismatch`);
	}

	const server = new OAuthServerAgent(metadata, dpopKey);
	const { info, token } = await server.exchangeCode(code, stored.verifier);

	const sub = info.sub;
	const session: Session = { dpopKey, info, token };

	await storeSession(sub, session);

	return session;
};
